import type { Logger } from "@brains/utils/logger";
import type { IShell } from "../interfaces";
import { EventEmitter } from "events";
import type { Plugin, PluginRegistrationContext } from "../interfaces";
import type { IDaemonRegistry } from "./daemon-types";
import type {
  PluginManager as IPluginManager,
  PluginInfo,
  PluginManagerEventMap,
} from "./types";
import { PluginStatus, PluginEvent } from "./types";
import { PluginError } from "../errors";
import { PluginLifecycle } from "./plugin-lifecycle";
import { DependencyResolver } from "./dependency-resolver";
import { CapabilityRegistrar } from "./capability-registrar";
import { Effect, Either } from "@brains/utils/effect";
import {
  ProjectionRegistry,
  type ProjectionGraph,
} from "../entity/projection-registry";
import type { ProjectionRule } from "../entity/projection-rule";

async function runConcurrentPhase(
  operations: Array<() => Promise<void>>,
): Promise<void> {
  const results = await Effect.runPromise(
    Effect.all(
      operations.map((operation) =>
        Effect.either(
          Effect.tryPromise({
            try: operation,
            catch: (error) => error,
          }),
        ),
      ),
      { concurrency: "unbounded" },
    ),
  );
  const firstFailure = results.find(Either.isLeft);
  if (firstFailure) throw firstFailure.left;
}

// Re-export enums for convenience
export { PluginEvent, PluginStatus } from "./types";

/**
 * Plugin manager that handles plugin registration, initialization, and lifecycle
 */
export class PluginManager implements IPluginManager {
  private plugins: Map<string, PluginInfo> = new Map();
  private logger: Logger;
  private events = new EventEmitter();
  private daemonRegistry: IDaemonRegistry;
  private shell: IShell | null = null;
  private initializedPluginIds: string[] = [];
  private pluginLifecycle: PluginLifecycle;
  private dependencyResolver: DependencyResolver;
  private capabilityRegistrar: CapabilityRegistrar;
  private readonly projectionRegistry = ProjectionRegistry.createFresh();
  private projectionGraph: ProjectionGraph | undefined;

  public static createFresh(
    logger: Logger,
    daemonRegistry: IDaemonRegistry,
  ): PluginManager {
    return new PluginManager(logger, daemonRegistry);
  }

  /**
   * Set the shell instance after it's created
   */
  public setShell(shell: IShell): void {
    this.shell = shell;
  }

  private constructor(logger: Logger, daemonRegistry: IDaemonRegistry) {
    this.logger = logger.child("PluginManager");
    this.events = new EventEmitter();
    this.daemonRegistry = daemonRegistry;

    // Initialize helper classes
    this.pluginLifecycle = new PluginLifecycle(
      this.plugins,
      this.events,
      this.daemonRegistry,
      this.projectionRegistry,
      logger,
    );
    this.dependencyResolver = new DependencyResolver(
      this.plugins,
      this.events,
      logger,
    );
    this.capabilityRegistrar = new CapabilityRegistrar(
      logger,
      this.projectionRegistry,
    );
  }

  /**
   * Register a plugin with the system
   * This only registers the plugin but doesn't initialize it
   */
  public registerPlugin(plugin: Plugin): void {
    if (!plugin.id) {
      throw new PluginError(
        "unknown",
        "Registration failed: Plugin must have an id",
      );
    }

    this.logger.debug(`Registering plugin: ${plugin.id} (${plugin.version})`);

    // Check if plugin is already registered
    if (this.plugins.has(plugin.id)) {
      const existingInfo = this.plugins.get(plugin.id);
      const existingVersion = existingInfo?.plugin.version;

      throw new PluginError(
        plugin.id,
        `Registration failed: Plugin is already registered with version ${existingVersion}`,
      );
    }

    // Get dependencies or use empty array
    const dependencies = plugin.dependencies ?? [];

    // Store plugin info
    const pluginInfo: PluginInfo = {
      plugin,
      status: PluginStatus.REGISTERED,
      dependencies,
    };

    this.plugins.set(plugin.id, pluginInfo);
    this.logger.debug(`Registered plugin: ${plugin.id} (${plugin.version})`);

    // Emit registered event
    this.events.emit(PluginEvent.REGISTERED, plugin.id, plugin);
  }

  /**
   * Initialize all registered plugins in dependency order
   * Plugins with no dependencies are initialized first
   */
  public async initializePlugins(
    registrationContext?: PluginRegistrationContext,
  ): Promise<void> {
    this.logger.debug("Initializing plugins...");

    this.initializedPluginIds = [];

    // Use dependency resolver to handle initialization order
    const result = await this.dependencyResolver.resolveInitializationOrder(
      async (pluginId) => {
        await this.initializePlugin(pluginId, registrationContext);
      },
    );

    this.logger.debug(
      `Initialized ${result.initialized.size} of ${this.plugins.size} plugins`,
    );
  }

  /**
   * Initialize a specific plugin
   */
  private async initializePlugin(
    pluginId: string,
    registrationContext?: PluginRegistrationContext,
  ): Promise<void> {
    if (!this.shell) {
      throw new PluginError(
        pluginId,
        "Cannot initialize plugin: Shell not set. Call setShell() first.",
      );
    }
    const shell = this.shell;

    try {
      const capabilities = await this.pluginLifecycle.initializePlugin(
        pluginId,
        shell,
        registrationContext,
      );

      await this.capabilityRegistrar.registerCapabilities(
        shell,
        pluginId,
        capabilities,
        { executionOnly: registrationContext?.executionOnly },
      );

      this.initializedPluginIds.push(pluginId);
    } catch (error) {
      await this.pluginLifecycle.failPluginInitialization(pluginId, error);
      throw error;
    }
  }

  /**
   * Finalize composition after all registrations and before content discovery.
   * Run in dependency order so dependent plugins observe finalized providers.
   */
  public async finalizePluginRegistrations(): Promise<void> {
    this.logger.debug("Finalizing plugin registrations...");
    for (const id of this.initializedPluginIds) {
      await this.pluginLifecycle.finalizePluginRegistration(id);
    }

    if (!this.shell) {
      throw new Error("Cannot validate projections before the shell is set");
    }
    const entityRegistry = this.shell.getEntityRegistry();
    const graph = this.projectionRegistry.validate(
      entityRegistry.getAllEntityTypes().map((type) => {
        const config = entityRegistry.getEntityTypeConfig(type);
        return {
          type,
          projectionSource:
            config.projectionSource !== false &&
            config.projectionSourceRole !== "excluded",
        };
      }),
    );
    this.projectionGraph = graph;
    for (const entry of graph.unknownSourceTypes) {
      this.logger.warn(
        "Projection declares entity source types no installed plugin registers",
        { projectionId: entry.projectionId, types: entry.types },
      );
    }
    this.logger.debug("Validated projection graph", {
      projections: graph.projections.length,
      edges: graph.edges.length,
    });
  }

  /** Internal read-only composition output for core runtime services. */
  public getProjectionGraphSnapshot(): ProjectionGraph {
    if (!this.projectionGraph) {
      throw new Error("Projection graph has not been finalized");
    }
    return this.projectionGraph;
  }

  /** Internal executable capabilities for the projection scheduler. */
  public getProjectionRulesSnapshot(): readonly ProjectionRule[] {
    if (!this.projectionGraph) {
      throw new Error("Projection graph has not been finalized");
    }
    return Object.freeze(this.projectionRegistry.listRules());
  }

  /**
   * Dispatch ready hooks for all initialized plugins.
   *
   * Hooks run concurrently — no ordering guarantees between plugins in this
   * phase. Plugins that need cross-plugin coordination must use the message
   * bus.
   */
  public async readyPlugins(): Promise<void> {
    this.logger.debug("Dispatching plugin ready hooks...");

    try {
      await runConcurrentPhase(
        this.initializedPluginIds.map(
          (id) => (): Promise<void> => this.pluginLifecycle.readyPlugin(id),
        ),
      );
    } catch (error) {
      for (const id of this.initializedPluginIds) {
        const pluginInfo = this.plugins.get(id);
        if (pluginInfo?.status === PluginStatus.ERROR) {
          await this.pluginLifecycle.failPluginInitialization(
            id,
            pluginInfo.error ?? error,
          );
        }
      }
      throw error;
    }
  }

  /**
   * Start daemons for all initialized plugins concurrently.
   */
  public async startPluginDaemons(): Promise<void> {
    this.logger.debug("Starting plugin daemons...");

    await runConcurrentPhase(
      this.initializedPluginIds.map(
        (id) => (): Promise<void> =>
          this.pluginLifecycle.startPluginDaemons(id),
      ),
    );
  }

  /**
   * Get a registered plugin by ID
   */
  public getPlugin(id: string): Plugin | undefined {
    const pluginInfo = this.plugins.get(id);
    return pluginInfo?.plugin;
  }

  /**
   * Get plugin status by ID
   */
  public getPluginStatus(id: string): PluginStatus | undefined {
    const pluginInfo = this.plugins.get(id);
    return pluginInfo?.status;
  }

  /**
   * Check if a plugin is registered
   */
  public hasPlugin(id: string): boolean {
    return this.plugins.has(id);
  }

  /**
   * Check if a plugin is initialized
   */
  public isPluginInitialized(id: string): boolean {
    const pluginInfo = this.plugins.get(id);
    return pluginInfo?.status === PluginStatus.INITIALIZED;
  }

  /**
   * Get all registered plugin IDs
   */
  public getAllPluginIds(): string[] {
    return Array.from(this.plugins.keys());
  }

  /**
   * Get all plugins with their status
   */
  public getAllPlugins(): Map<string, PluginInfo> {
    return new Map(this.plugins);
  }

  /**
   * Get plugins that failed to initialize
   */
  public getFailedPlugins(): Array<{ id: string; error: Error }> {
    return Array.from(this.plugins.entries())
      .filter(
        (entry): entry is [string, PluginInfo & { error: Error }] =>
          entry[1].status === PluginStatus.ERROR &&
          entry[1].error !== undefined,
      )
      .map(([id, info]) => ({ id, error: info.error }));
  }

  /**
   * Get plugin package name by ID
   */
  public getPluginPackageName(pluginId: string): string | undefined {
    const pluginInfo = this.plugins.get(pluginId);
    return pluginInfo?.plugin.packageName;
  }

  /** Terminally release a plugin and its runtime resources. */
  public async disablePlugin(id: string): Promise<void> {
    await this.pluginLifecycle.disablePlugin(id);
  }

  /** Release plugins in reverse initialization order. */
  public async shutdownPlugins(): Promise<void> {
    const released = new Set<string>();
    for (const id of [...this.initializedPluginIds].reverse()) {
      await this.disablePlugin(id);
      released.add(id);
    }
    for (const id of [...this.plugins.keys()].reverse()) {
      if (!released.has(id)) await this.disablePlugin(id);
    }
  }

  /**
   * Subscribe to plugin events
   */
  public on<E extends PluginEvent>(
    event: E,
    listener: (...args: PluginManagerEventMap[E]) => void,
  ): void {
    this.events.on(event, listener);
  }

  /**
   * Subscribe to plugin events once
   */
  public once<E extends PluginEvent>(
    event: E,
    listener: (...args: PluginManagerEventMap[E]) => void,
  ): void {
    this.events.once(event, listener);
  }

  /**
   * Unsubscribe from plugin events
   */
  public off<E extends PluginEvent>(
    event: E,
    listener: (...args: PluginManagerEventMap[E]) => void,
  ): void {
    this.events.off(event, listener);
  }

  /**
   * Get the event emitter for external subscribers
   */
  public getEventEmitter(): EventEmitter {
    return this.events;
  }
}
